import { Capacitor } from '@capacitor/core';
import { RiCloseLine } from '@remixicon/react';
import { forwardRef, memo, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useEnrollContext } from '../../../contexts/Enroll/EnrollContext';
import { choosePlatform, renderPosition } from '../../../helpers/utils';
import { OptionsTournament } from '../../../types/types';
import { Captain } from '../../Icons/Captain';
import { Checked } from '../../Icons/Checked';
import { Points } from '../../Icons/Points';
import { Unchecked } from '../../Icons/Unchecked';
import PlayersSelect from '../../Modal/PlayersSelect';
import Tooltip from '../../Tooltip';
import Button from '../../ui/button';
import Toast from '../../ui/toast';
import { GamePlanField } from '../../Widgets/GamePlanField';
import { GamePlanFieldView } from '../../Widgets/GamePlanField/view';

interface OptionItemProps {
	item: OptionsTournament;
	scoutsAverage: any;
	index: number;
	playersTeams: any;
	optionsSelect: any;
	blockSelection: boolean;
	onChange: (id: any) => void;
}

const OptionItem = memo(
	forwardRef<HTMLDivElement, OptionItemProps>(
		(
			{
				item,
				index,
				playersTeams,
				optionsSelect,
				blockSelection,
				scoutsAverage,
				onChange,
			},
			ref
		) => {
			const { enrollOptions } = useEnrollContext();
			const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
			// afirmacao
			const [statementReply, setStatementReply] = useState<string>('');
			// draft
			const [open, setClose] = useState(false);
			const [draftReply, setDraftReply] = useState<any>([]);
			const [draftReplySelect, setDraftReplySelect] = useState<any>([]);
			const [error, setError] = useState('');
			const [disableButton, setDisableButton] = useState(true);

			const draftPlayersQtd =
				item.optionType === 1 &&
				item.draftPositions.reduce(
					(total: any, selectPosition: any) => {
						total += selectPosition.quantity;
						return total;
					},
					0
				);
			const selectedPlayers = playersTeams
				.filter((player: any) =>
					draftReply.some(
						(draft: any) => draft.playerId === player.player.id
					)
				)
				.sort((a: any, b: any) => a.position - b.position);

			const isSelected = optionsSelect.some(
				(option: any) => option.optionId === item.id
			);
			const countSelectPlayerDraft = draftReplySelect.reduce(
				(acc: any, item: any) => acc + (!item.isReserve ? 1 : 0),
				0
			);
			const kicksCount = draftReplySelect.filter(
				(item: any) => item?.kicks
			).length;

			useEffect(() => {
				const findExists = enrollOptions.find(
					(option: any) => option.optionId === item.id
				);

				if (findExists) {
					if (findExists.optionType === 0) {
						setStatementReply(
							findExists.statementReply ? 'Yes' : 'Não'
						);
					}
					if (findExists.optionType === 1) {
						setDraftReply(findExists.draftReply);
						setDraftReplySelect(findExists.draftReply);
					}
				}
			}, []);

			const handleChangeStatement = (data: string) => {
				if (blockSelection && !isSelected) {
					if (toastTimeoutRef.current) {
						clearTimeout(toastTimeoutRef.current);
					}

					toastTimeoutRef.current = setTimeout(() => {
						toast(
							<Toast variant="error">
								Desmarque uma pergunta para selecionar uma nova
								pergunta.
							</Toast>
						);
					}, 1000);
					return;
				}
				setStatementReply(data);
				onChange({
					optionId: item.id,
					statementReply: data === 'Yes' ? true : false,
					optionType: item.optionType,
					sequence: item.sequence,
				});
			};
			const handleChangeDraft = (
				key: string,
				playerId: any,
				teamId: any,
				isReserve: boolean
			) => {
				const existingIndex = draftReplySelect.findIndex(
					(item: any) => item.key === key
				);
				if (existingIndex !== -1) {
					const updatedDraft = [...draftReplySelect];
					updatedDraft[existingIndex] = {
						key,
						playerId,
						teamId,
						isReserve,
					};
					setDraftReplySelect(updatedDraft);
					return;
				}

				const newPlayer = playersTeams.find(
					(player: any) => player.player.id === playerId
				);
				const newTeamId = newPlayer.team.id;
				const newPlayerCanRepeatTeam = item.canRepeatTeam;

				let updatedDraft = [...draftReplySelect];
				if (!isReserve) {
					updatedDraft = updatedDraft.filter(
						(item: any) =>
							!(item.playerId === playerId && item.isReserve)
					);

					if (newPlayerCanRepeatTeam === 'Não') {
						const duplicateTeam = updatedDraft
							.filter((item) => !item.isReserve)
							.some((item) => {
								const selectedPlayer = playersTeams.find(
									(p: any) => p.player.id === item.playerId
								);
								return selectedPlayer?.team.id === newTeamId;
							});
						if (duplicateTeam) {
							return toast(
								<Toast variant="error">
									Você não pode selecionar mais de 1 jogador
									do mesmo time na escalação principal.
								</Toast>
							);
						}
					}

					if (countSelectPlayerDraft >= draftPlayersQtd) {
						return toast(
							<Toast variant="error">
								Você já selecionou todos os jogadores titulares.
							</Toast>
						);
					}
				} else {
					if (newPlayerCanRepeatTeam === 'Não') {
						const alreadySelectedAsStarting = updatedDraft.some(
							(item: any) =>
								!item.isReserve && item.playerId === playerId
						);
						if (alreadySelectedAsStarting) {
							return toast(
								<Toast variant="error">
									O jogador não pode ser selecionado como
									reserva se já estiver na escalação
									principal.
								</Toast>
							);
						}
					}
				}

				setDraftReplySelect([
					...updatedDraft,
					{
						key,
						playerId,
						teamId,
						isReserve,
					},
				]);
			};
			const removeDraft = (key: string) => {
				const remove = draftReplySelect.filter(
					(item: any) => item.key !== key
				);
				setDraftReplySelect(remove);
			};
			const addKicksToDraft = (key: string) => {
				const selectionLimit = Number(item.numbersOfKicks);
				const currentItem = draftReplySelect.find(
					(item: any) => item.key === key
				);

				if (currentItem && currentItem.kicks) {
					const updatedDraft = draftReplySelect.map((item: any) =>
						item.key === key ? { ...item, kicks: false } : item
					);
					setDraftReplySelect(updatedDraft);
				} else {
					const countSelected = draftReplySelect.filter(
						(item: any) => item?.kicks
					).length;
					if (countSelected < selectionLimit) {
						const updatedDraft = draftReplySelect.map((item: any) =>
							item.key === key ? { ...item, kicks: true } : item
						);
						setDraftReplySelect(updatedDraft);
					} else {
						toast(
							<Toast variant="warning">
								Numero maximo de capitão atingido!
							</Toast>
						);
					}
				}
			};

			useEffect(() => {
				if (countSelectPlayerDraft < Number(draftPlayersQtd)) {
					const countSelect =
						Number(draftPlayersQtd) - countSelectPlayerDraft;
					setError(
						`Escale ${countSelect} ${
							countSelect === 1 ? 'jogador' : 'jogadores'
						} `
					);
					setDisableButton(true);
					return;
				} else if (
					Number(item.numbersOfKicks) !== 0 &&
					kicksCount === 0
				) {
					setError('Defina seu capitão');
					setDisableButton(true);
					return;
				} else {
					setDisableButton(false);
					setError('');
				}
			}, [countSelectPlayerDraft, kicksCount]);

			const handleSaveDraft = () => {
				if (countSelectPlayerDraft < Number(draftPlayersQtd)) {
					const countSelect =
						Number(draftPlayersQtd) - countSelectPlayerDraft;

					return toast(
						<Toast variant="error">
							Selecione {countSelect}{' '}
							{countSelect > 1
								? 'jogadores titulares'
								: 'jogador titular'}
							!
						</Toast>
					);
				}

				if (Number(item.numbersOfKicks) !== 0 && kicksCount === 0) {
					return toast(
						<Toast variant="error">
							Selecione pelo menos {Number(item.numbersOfKicks)}{' '}
							{Number(item.numbersOfKicks) > 1
								? 'capitões'
								: 'capitão'}
							!
						</Toast>
					);
				}

				setDraftReply(draftReplySelect);
				onChange({
					optionId: item.id,
					optionType: item.optionType,
					sequence: item.sequence,
					draftReply: draftReplySelect,
				});
				setClose(!open);
			};
			const removeChangeEnroll = (id: string) => {
				if (item.optionType === 0) {
					setStatementReply('');
					onChange({
						optionId: item.id,
						delete: true,
					});
					return;
				}
				if (item.optionType === 1) {
					setDraftReply([]);
					setDraftReplySelect([]);
					onChange({
						optionId: item.id,
						delete: true,
					});
					return;
				}
			};

			const renderOption = (optionType: number, teams?: any) => {
				switch (optionType) {
					case 0:
						return (
							<div className="justify-between items-center gap-2 inline-flex">
								<div className="self-stretch justify-start items-center gap-2.5 inline-flex">
									<div className="text-custom-gray-12 text-[10px] font-semibold font-bahnschrift flex gap-1">
										<div className="flex flex-row items-center font-bahnschrift text-neutral-400 text-xs gap-1">
											{teams &&
												teams.length > 0 &&
												teams?.map(
													(
														team: any,
														index: number
													) => (
														<>
															{index === 1 && 'x'}
															<img
																key={index}
																className="w-6 h-6 object-scale-down"
																src={team.image}
																alt={team.name}
															/>
														</>
													)
												)}
										</div>
									</div>
								</div>

								<div className="grid grid-cols-2">
									<div
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleChangeStatement('Yes');
										}}
										className="flex justify-center items-center"
									>
										<div
											className={`flex gap-2 justify-start items-center py-2 rounded-[68px] transition-transform duration-300 ${
												statementReply === 'Yes'
													? 'bg-[#cccccc] scale-110 px-5'
													: 'scale-100 px-2'
											}`}
										>
											<div className="flex justify-start items-center gap-1.5 cursor-pointer">
												{statementReply === 'Yes' ? (
													<Checked />
												) : (
													<Unchecked />
												)}
												<div className="text-custom-black mr-2 text-sm font-semibold font-bahnschrift">
													Sim
													<div className="w-40 text-[#595959] absolute -mt-2 ml-2 text-[8px] leading-5 font-semibold font-bahnschrift">
														+{item.winPointsYes} pts
													</div>
												</div>
											</div>
										</div>
									</div>

									<div
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleChangeStatement('No');
										}}
										className="flex justify-center items-center"
									>
										<div
											className={`flex gap-2 justify-start items-center py-2 rounded-[68px] transition-transform duration-300 ${
												statementReply === 'No'
													? 'bg-[#cccccc] scale-110 px-5 ml-2'
													: 'scale-100 px-2'
											}`}
										>
											<div className="flex justify-start items-center gap-1.5 cursor-pointer">
												{statementReply === 'No' ? (
													<Checked />
												) : (
													<Unchecked />
												)}
												<div className="text-custom-black text-sm mr-2 font-semibold font-bahnschrift">
													Não
													<div className="w-40 text-[#595959] absolute -mt-2 ml-2 text-[8px] leading-5 font-semibold font-bahnschrift">
														+{item.winPointsNo} pts
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						);
					case 1:
						return (
							<div className="justify-between flex-col gap-4 flex">
								<div className="w-full flex flex-row justify-beetwen items-center gap-2.5 mt-3">
									<div className="w-full text-custom-gray-12 text-xs font-semibold font-bahnschrift flex gap-1 mt-3 -mb-0.5">
										{item.draftPositions.map(
											(pos: any, index: number) => (
												<div
													key={index}
													className="text-custom-black mr-2 text-sm font-semibold font-bahnschrift"
												>
													{renderPosition(
														pos.position
													)}
													<div className="text-[#595959] -mt-2 justify-end w-full flex text-[10px] leading-5 font-semibold font-bahnschrift">
														{pos.quantity}x
													</div>
												</div>
											)
										)}
									</div>
									<Button
										className="py-2 px-3"
										variant="black"
										textCustom="text-custom-white text-xs"
										onClick={() => {
											if (blockSelection && !isSelected) {
												if (toastTimeoutRef.current) {
													clearTimeout(
														toastTimeoutRef.current
													);
												}

												toastTimeoutRef.current =
													setTimeout(() => {
														toast(
															<Toast variant="error">
																Desmarque uma
																pergunta para
																selecionar!
															</Toast>
														);
													}, 1000);

												return;
											}
											setClose(!open);
										}}
									>
										Escalar
									</Button>
								</div>

								{draftReplySelect && (
									<GamePlanFieldView
										selectedPlayers={selectedPlayers}
										positions={item.draftPositions}
										players={playersTeams}
										draftReplySelect={draftReplySelect}
										numbersOfKicks={Number(
											item.numbersOfKicks
										)}
										modalityName={item.modality.name}
										scoutsAverage={scoutsAverage}
									/>
								)}

								<PlayersSelect
									id={item.id}
									open={open}
									onClose={() => {
										setClose(!open);
										const findExists = enrollOptions.find(
											(option: any) =>
												option.optionId === item.id
										);

										if (findExists) {
											setDraftReply(
												findExists.draftReply
											);
											setDraftReplySelect(
												findExists.draftReply
											);
										} else {
											setDraftReply([]);
											setDraftReplySelect([]);
										}
									}}
								>
									<div
										className={
											'flex flex-col justify-between px-6 lg:py-0 gap-4 pb-8 h-full overflow-y-auto ' +
											(Capacitor.getPlatform() === 'ios'
												? 'pt-safe-offset-6'
												: 'pt-14')
										}
									>
										<div className="flex flex-col gap-3 h-fit">
											<div className="flex flex-row justify-between items-center">
												<div>
													<span className="text-[#ececec] text-lg font-normal font-bahnschrift">
														Jogadores Titulares
													</span>
													<span className="text-[#6b6e74] text-xs font-light pl-1 font-bahnschrift">
														(
														{countSelectPlayerDraft}
														/{draftPlayersQtd})
													</span>
												</div>

												<div>
													{item.draftScout && (
														<Tooltip
															icon={
																<Points className="w-5 h-5 mr-1.5" />
															}
															title="Pontuação"
															infoIcon="success"
															drafPoints
															draftScouts={
																item.draftScout
																	?.scoutsRegistrations
															}
														/>
													)}
													{Number(
														item.numbersOfKicks
													) > 0 && (
														<Tooltip
															icon={
																<Captain
																	colorBg="#ffffff"
																	colorIcon={
																		choosePlatform()
																			? '#35168C'
																			: '#DF1737'
																	}
																	className="w-5 h-5 mr-1.5"
																/>
															}
															title="Capitão"
															infoIcon="warning"
															message={`Nesta equipe, você pode escolher ${Number(
																item.numbersOfKicks
															)} ${
																Number(
																	item.numbersOfKicks
																) > 1
																	? 'capitões'
																	: 'capitão'
															}, e a pontuação dele será dobrada.`}
														/>
													)}
												</div>
											</div>

											<GamePlanField
												optionTournament={item.id}
												positions={item.draftPositions}
												numbersOfKicks={Number(
													item.numbersOfKicks
												)}
												players={playersTeams}
												countSelectPlayerDraft={
													countSelectPlayerDraft
												}
												draftPlayersQtd={
													draftPlayersQtd
												}
												draftReplySelect={
													draftReplySelect
												}
												newPlayerCanRepeatTeam={
													item.canRepeatTeam
												}
												modalityName={
													item.modality.name
												}
												scoutsAverage={scoutsAverage}
												onChange={handleChangeDraft}
												removeDraft={removeDraft}
												addKicks={addKicksToDraft}
											/>
										</div>

										<div className="flex flex-row gap-4 mt-4 lg:pb-6">
											<Button
												className="px-5 py-3 border border-custom-white"
												variant="black"
												textCustom="text-custom-white"
												onClick={() => {
													setClose(!open);
													const findExists =
														enrollOptions.find(
															(option: any) =>
																option.optionId ===
																item.id
														);

													if (findExists) {
														setDraftReply(
															findExists.draftReply
														);
														setDraftReplySelect(
															findExists.draftReply
														);
													} else {
														setDraftReply([]);
														setDraftReplySelect([]);
													}
												}}
											>
												Fechar
											</Button>
											<Button
												className="w-full py-3"
												textCustom="text-custom-white"
												onClick={handleSaveDraft}
												disabled={disableButton}
											>
												{error
													? error
													: 'Finalizar Escalação'}
											</Button>
										</div>
									</div>
								</PlayersSelect>
							</div>
						);
					default:
						return null;
				}
			};
			const selectedItem = () => {
				if (item.optionType === 0 && statementReply !== '') {
					return true;
				}
				if (
					item.optionType === 1 &&
					draftReply.filter((count: any) => !count.isReserve)
						.length === parseInt(draftPlayersQtd)
				) {
					return true;
				}
				return false;
			};

			return (
				<div
					ref={ref}
					tabIndex={-1}
					key={index}
					className="flex flex-col w-full lg:max-w-screen-xl lg:mx-auto"
				>
					<div
						className={
							'bg-custom-white1 min-h-[116px] rounded-lg ' +
							(selectedItem() ? 'border-2 border-primary' : '')
						}
					>
						<div
							className={
								'absolute w-7 h-7 bg-primary rounded-tl-[5px] rounded-br-[9px] justify-center items-center inline-flex cursor-pointer ' +
								(selectedItem() ? '' : 'hidden')
							}
							onClick={() => removeChangeEnroll(item.id)}
						>
							<RiCloseLine className="w-4 h-4 justify-center items-center flex text-custom-white1" />
						</div>
						<div
							className="flex flex-col gap-0.5 px-4 py-1.5"
							onClick={() => {
								if (item.optionType === 1) {
									if (blockSelection && !isSelected) {
										if (toastTimeoutRef.current) {
											clearTimeout(
												toastTimeoutRef.current
											);
										}

										toastTimeoutRef.current = setTimeout(
											() => {
												toast(
													<Toast variant="error">
														Desmarque uma pergunta
														para selecionar uma nova
														pergunta.
													</Toast>
												);
											},
											1000
										);
										return;
									}
									setClose(!open);
								}
							}}
						>
							<div
								className={
									'self-stretch justify-between items-center gap-2.5 inline-flex ' +
									(selectedItem() ? 'pl-4' : '')
								}
							>
								{item.optionType !== 1 ? (
									<div
										className={
											selectedItem() ? 'pt-4' : 'pt-2'
										}
									/>
								) : (
									<div></div>
								)}
								{item.optionType === 1 && (
									<div className="flex gap-1.5 mt-1 -mb-1">
										<Tooltip
											icon={
												<Points className="w-4 h-4 " />
											}
											title="Pontuação"
											infoIcon="success"
											drafPoints
											draftScouts={
												item.draftScout
													?.scoutsRegistrations
											}
										/>
										{Number(item.numbersOfKicks) > 0 && (
											<Tooltip
												icon={
													<Captain
														colorBg="#ffffff"
														colorIcon={
															choosePlatform()
																? '#35168C'
																: '#DF1737'
														}
														className="w-4 h-4"
													/>
												}
												title="Capitão"
												infoIcon="warning"
												message={`Nesta equipe, você pode escolher ${Number(
													item.numbersOfKicks
												)} ${
													Number(
														item.numbersOfKicks
													) > 1
														? 'capitões'
														: 'capitão'
												}, e a pontuação dele será dobrada.`}
											/>
										)}
									</div>
								)}
							</div>
							<div className="self-stretch flex-col justify-start items-start gap-0.5 flex">
								<div
									className={
										'self-stretch flex-col justify-start items-start gap-1 flex' +
										(item.optionType !== 0
											? ''
											: ' min-h-12')
									}
								>
									<div className="self-stretch text-custom-black text-base font-semibold font-bahnschrift">
										{item.title}
									</div>
								</div>
							</div>

							{renderOption(item.optionType, item.teams)}
						</div>
					</div>
				</div>
			);
		}
	)
);

export default OptionItem;
